import { useGetTaskQuery } from 'api/collaborateApi';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useResponsive } from 'utils/useMediaQuery';
import {
  addLostTask,
  clearPendingTask,
  getCreateModalOpen,
  getGeneralTaskModalOpen,
  getLostTasks,
  getRealTasks,
  getSelectedTaskId,
  setCreateModalOpen,
  setGeneralTaskModalOpen,
  setSelectedTask,
  setShouldShowSkeleton,
} from '../store/tasksSlice';
import { getDisplayMode } from '../store/uiSlice';
import { scrollToTaskElement } from '../utils/node';

interface UseTaskDetailsOptions {
  enableSearchParamHandling?: boolean;
}

/**
 * Combined hook to fetch task details and optionally handle search param-based task opening.
 * Ensures the query is only fired once while supporting both use cases.
 */
export const useTaskDetails = (options: UseTaskDetailsOptions = {}) => {
  const { enableSearchParamHandling = true } = options;
  const dispatch = useDispatch();
  const selectedTaskId = useSelector(getSelectedTaskId);
  const displayMode = useSelector(getDisplayMode);
  const createModalOpen = useSelector(getCreateModalOpen);
  const generalTaskModalOpen = useSelector(getGeneralTaskModalOpen);
  const lostTasks = useSelector(getLostTasks);
  const realTasks = useSelector(getRealTasks);
  const { isMobile } = useResponsive();

  // Determine if we're ready based on having real tasks
  const ready = realTasks.length > 0;
  // Determine if we should skip the query
  const shouldSkip =
    !selectedTaskId ||
    selectedTaskId.includes('OPTIMISTIC') ||
    selectedTaskId.includes('PENDING');

  // Single query instance
  const query = useGetTaskQuery(selectedTaskId ?? '', {
    skip: shouldSkip,
  });

  const { currentData: taskDetails, isLoading } = query;

  // Effect for updating Redux store with task details
  useEffect(() => {
    if (!taskDetails) return;

    if (selectedTaskId !== taskDetails.result.task_id) {
      dispatch(setSelectedTask(taskDetails.result));
      dispatch(setShouldShowSkeleton(false));
    } else {
      dispatch(setSelectedTask(taskDetails.result));
    }
  }, [dispatch, taskDetails, selectedTaskId]);

  // Effect for handling search param-based task opening (optional)
  useEffect(() => {
    if (!enableSearchParamHandling) return;

    const handleTaskOpen = async () => {
      if (selectedTaskId && taskDetails && displayMode === 'desktop' && ready) {
        dispatch(clearPendingTask());

        // Only open modals if none are currently open
        if (!createModalOpen && !generalTaskModalOpen) {
          // Check if it's a general task
          const isGeneralTask = taskDetails.result.task_type.some(
            (type) => type === 'General'
          );

          // If it's a general task, we already know which modal to use
          if (isGeneralTask) {
            dispatch(setGeneralTaskModalOpen(selectedTaskId));
            return;
          }

          // For non-general tasks, check if this is a lost task or if the element exists
          const isKnownLostTask = lostTasks.includes(selectedTaskId);

          // If we already know it's lost, use the general modal
          if (isKnownLostTask) {
            dispatch(setGeneralTaskModalOpen(selectedTaskId));
            return;
          }

          // Otherwise, check if the element exists before deciding which modal to open
          if (taskDetails.result.task_element_path) {
            // Try to scroll to the element - if it succeeds, use create modal
            // If it fails (element not found/visible), the element is considered lost
            try {
              await scrollToTaskElement(taskDetails.result.task_element_path);
              // If we get here, the element exists and is visible
              dispatch(setCreateModalOpen(selectedTaskId));
            } catch (error) {
              // Element doesn't exist or isn't visible, treat as lost task
              console.warn(
                'Element not found or not visible, treating as lost task:',
                error
              );
              dispatch(addLostTask(selectedTaskId));
              dispatch(setGeneralTaskModalOpen(selectedTaskId));
            }
          } else {
            // No element path, use create modal as fallback
            dispatch(setCreateModalOpen(selectedTaskId));
          }
        }
      } else if (selectedTaskId && displayMode !== 'desktop') {
        // Send to iframe
        window.parent.postMessage(
          {
            type: 'OPEN_TASK',
            taskId: selectedTaskId,
          },
          '*'
        );
      }
    };

    handleTaskOpen();
  }, [
    enableSearchParamHandling,
    ready,
    selectedTaskId,
    taskDetails,
    dispatch,
    displayMode,
    isMobile,
    createModalOpen,
    generalTaskModalOpen,
    lostTasks,
  ]);

  return {
    ...query,
    taskDetails,
    isLoading,
    taskId: selectedTaskId,
  };
};
